import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/useMediaQuery";

interface ResponsiveModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Modal title — rendered as DialogTitle / SheetTitle */
  title: React.ReactNode;
  /** Optional subtitle — rendered as DialogDescription / SheetDescription */
  description?: React.ReactNode;
  /** Scrollable body content */
  children: React.ReactNode;
  /**
   * Sticky action area.
   * • Mobile: pinned above safe-area at bottom of sheet.
   * • Desktop: rendered inside DialogFooter below scrollable content.
   */
  footer?: React.ReactNode;
  /** Desktop max-width override (Tailwind class). Defaults to sm:max-w-[500px]. */
  maxWidth?: string;
  onPointerDownOutside?: React.ComponentProps<typeof DialogContent>["onPointerDownOutside"];
}

/**
 * ResponsiveModal
 *
 * Renders a standard Dialog on desktop (≥ 768 px) and a full-height
 * bottom Sheet on mobile (< 768 px).
 *
 * Both variants preserve Radix focus-trap, Escape-to-close, and
 * aria-labelledby / aria-describedby automatically.
 */
export function ResponsiveModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  maxWidth = "sm:max-w-[500px]",
  onPointerDownOutside,
}: ResponsiveModalProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  /* ── Desktop: centred Dialog ─────────────────────────────────────────── */
  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn("max-h-[90vh] overflow-y-auto", maxWidth)}
          aria-describedby={description ? "rm-desc" : undefined}
          onPointerDownOutside={onPointerDownOutside}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description && (
              <DialogDescription id="rm-desc">{description}</DialogDescription>
            )}
          </DialogHeader>
          {children}
          {footer && <DialogFooter>{footer}</DialogFooter>}
        </DialogContent>
      </Dialog>
    );
  }

  /* ── Mobile: full-height bottom Sheet ───────────────────────────────── */
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        /*
         * !p-0 overrides the p-6 from sheetVariants so we control padding
         * entirely inside this component.
         * gap-0 removes the default gap-4 between items.
         */
        className="h-[95dvh] rounded-t-2xl !p-0 flex flex-col gap-0"
        aria-describedby={description ? "rm-desc" : undefined}
        onPointerDownOutside={onPointerDownOutside}
      >
        {/* Drag handle affordance */}
        <div
          className="mx-auto mt-3 mb-1 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/30"
          aria-hidden="true"
        />

        <SheetHeader className="px-4 py-3 shrink-0 border-b text-left space-y-1">
          <SheetTitle>{title}</SheetTitle>
          {description && (
            <SheetDescription id="rm-desc">{description}</SheetDescription>
          )}
        </SheetHeader>

        {/* Scrollable body — overscroll-contain prevents page scroll chaining */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4">
          {children}
        </div>

        {/* Sticky footer — clears safe-area via pb-safe */}
        {footer && (
          <div className="shrink-0 border-t bg-background p-4 pb-safe">
            {footer}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
